/**
 * Project name generation utility
 *
 * Calls the AI backend to generate concise, descriptive project names
 * based on user prompts. Falls back to null on any error to allow
 * client-side fallback logic.
 */

import { getBackendUrl } from "./backendUrl";

const AI_BACKEND_URL = getBackendUrl();

export interface GenerateNameResponse {
  name: string;
  fallback: boolean;
}

/**
 * Generate a project name using LLM based on user's prompt.
 *
 * @param prompt - User's initial prompt or project description
 * @returns Generated project name, or null if generation failed
 *
 * @example
 * const name = await generateProjectNameViaLLM("Create an e-commerce system architecture diagram");
 * // Returns: "E-commerce System Architecture"
 *
 * @example
 * const name = await generateProjectNameViaLLM("Create a user login flow");
 * // Returns: "User Login Flow"
 */
export async function generateProjectNameViaLLM(
  prompt: string,
): Promise<string | null> {
  if (!prompt || prompt.trim().length < 2) {
    return null;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    const response = await fetch(
      `${AI_BACKEND_URL}/v1/ai/generate-project-name`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ prompt: prompt.trim() }),
        signal: controller.signal,
      },
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      let errorBody = "";
      try {
        errorBody = await response.text();
      } catch {
        // ignore
      }
      console.warn("[Naming] generate-project-name API error", {
        status: response.status,
        body: errorBody?.substring(0, 200),
      });
      return null;
    }

    const data: GenerateNameResponse = await response.json();

    if (!data.name || data.name.trim().length < 2) {
      return null;
    }

    return data.name.trim().replace(/\s+/g, " ");
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    const name = error instanceof Error ? error.name : "Error";
    if (error instanceof Error && error.name === "AbortError") {
      console.error("[Naming] generate-project-name timeout (6s)");
    } else {
      console.error(`[Naming] generate-project-name error: ${name} - ${msg}`);
    }
    return null;
  }
}
